<template>
  <div ref="index">
    <section class="taskapp">
      <!-- Native: both v-ifs below collapse the toolbar band entirely when
           none of its children would render (search hides <=5 tasks,
           sort/filter hide <=1 task). Without them the empty Fomantic menu
           still occupies ~58px + margins -- the dead band that pushed the
           capture card ~170px down the screen (assessment: Tasks #2). Web
           keeps today's markup: /tasks always passes show-search, and the
           event-modal embed keeps its band for layout parity. -->
      <div
        v-if="!isNativeApp || toolbarHasItems || tasks.length > 1"
        class="clearing margin-bottom-small"
      >
        <div
          v-if="
            (showSearch || showFilters || currentUserId) &&
            (!isNativeApp || toolbarHasItems)
          "
          class="ui right floated secondary menu tiny stackable"
          :class="{ secondary: !showSearch }"
        >
          <div
            v-if="showSearch && (!isNativeApp || tasks.length > 5)"
            class="ui search item fitted"
          >
            <div :class="{ loading }" class="ui icon input">
              <input
                v-model="searchString"
                v-debounce.cancelOnEmpty="300"
                type="text"
                placeholder="Search ..."
                @keypress.enter="loadPage(1)"
                @change="loadPage(1)"
              />

              <i
                v-if="searchString.length < 1"
                class="search link icon"
                @click="loadPage(1)"
              />
              <i v-else class="delete link icon" @click="clearSearch" />
            </div>
          </div>

          <div
            v-if="tasks.length > 1 && Object.keys(availableSorts).length > 1"
            class="ui dropdown white item sorts"
          >
            <span class="capitalize">
              Sort: <span class="nobreak">{{ selectedSortName }}</span>
            </span>
            <div class="menu">
              <div
                v-for="(sort, key) in availableSorts"
                :key="key"
                class="item"
                :class="{ active: selectedSort == key }"
                @click="selectedSort = key"
              >
                {{ sort.name }}
              </div>
            </div>
          </div>

          <div
            v-if="tasks.length > 1 && showFilters"
            class="ui dropdown white item filters"
          >
            <span class="capitalize"
              >Filter: <span class="nobreak">{{ filter }}</span></span
            >
            <filters :selected="filter" @change="changeFilter" />
          </div>
        </div>
        <h4
          v-if="tasks.length > 1"
          class="ui left floated header padding-top-small"
        >
          Tasks
        </h4>
      </div>

      <header v-if="allowAdditions" class="header">
        <div class="ui form segment">
          <task-input
            ref="newTaskInput"
            :placeholder="
              isNativeApp ? 'Add a task…' : 'Enter a task here'
            "
            :task="newTask"
            :users="users"
            :allow-cancel="false"
            :parent-event="parentEvent"
          />

          <a
            v-if="suggestedEventTasks.length > 0"
            href="#"
            @click.stop.prevent="showSuggested = !showSuggested"
          >
            <i
              class="icon caret fitted"
              :class="{
                right: !showSuggested,
                down: showSuggested,
              }"
            />
            Load Suggested Tasks
          </a>

          <div
            v-show="suggestedEventTasks.length > 0 && showSuggested"
            class="ui message"
          >
            <div class="ui list">
              <div v-for="task in suggestedEventTasks" class="task item">
                <div class="content">
                  <div class="ui checkbox left floated checked">
                    <input class="hidden" type="checkbox" checked />
                    <label>
                      <span>{{ task.name }}</span>
                    </label>
                  </div>
                </div>

                <div class="ui right floated">
                  <avatar
                    v-if="task.user_login"
                    class="ui image avatar"
                    :title="task.user_login"
                    :username="task.user_login"
                    :src="task.user_gravatar"
                    size="26"
                  />
                </div>
              </div>
            </div>
            <a
              class="ui button"
              @click.stop.prevent="addSuggestedTasks(suggestedEventTasks)"
            >
              <i class="ui icon list" />
              Add these tasks
            </a>

            <div class="ui simple dropdown" v-if="users.length > 1">
              <i class="icon grey dropdown"></i>
              <div class="menu icon buttons left">
                <div class="header">
                  <i class="icon exchange"></i>
                  Assign all to:
                </div>
                <div
                  class="item"
                  :key="`${user.id}-suggested-task-selection`"
                  v-for="user in this.users"
                  @click.stop.prevent="assignSuggestedTasksToUser(user)"
                >
                  {{ user.login }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <!--
        One empty state for every surface.

        This used to be split on isNativeApp. Non-native got a bare Fomantic
        slab reading "You have no tasks. Rock on." with a raised-fist icon and
        NO action; the app got a real empty state. The split was not defensible
        in either direction: the "Rock on" copy is the cheap / consumer-app cute
        register DESIGN.md section 6 rules out, and an empty state with nothing
        to do next is the one thing an empty state exists to avoid.

        So this is a deletion, not a width partition -- the good empty state is
        simply everyone's now, desktop included. Its styles were never
        native-scoped (`.taskapp > .native-tasks-empty` is an ordinary scoped
        component rule), so nothing else had to move.
      -->
      <div v-if="tasks.length < 1" class="native-tasks-empty">
        <i class="check circle outline icon" />
        <h3>No tasks yet</h3>
        <p>
          Capture to-dos for your campaigns — assign them and set due dates.
        </p>
        <!-- Empty states carry their primary action (craft item 5): hands
             focus to the capture input above rather than leaving the reader
             to connect the two. -->
        <a
          v-if="allowAdditions"
          class="ui button native-tinted-btn native-tasks-empty-action"
          @click.stop.prevent="focusNewTask"
        >
          Add your first task
        </a>
      </div>

      <section v-if="tasks.length > 0" :class="{ loading }" class="ui form">
        <small v-if="selectedUser">
          Showing {{ selectedUser.login }}'s tasks
          <a href="#" @click.stop.prevent="showAll()"> (show all) </a>
        </small>

        <task-list
          class="clearboth"
          :users="users"
          :tasks="filteredTasks"
          :group="group"
          :parent-event="parentEvent"
          :filter="filter"
          @selectUser="showUsersTasks"
          @loadPage="loadPage"
        />
      </section>
    </section>

    <div v-if="showSaveAll">
      <a class="ui button primary" @click.stop.prevent="saveAll">
        Save All Tasks
      </a>
    </div>
  </div>
</template>

<script>
import Vue from "vue/dist/vue.esm";
import { mapState, mapGetters } from "vuex";
import EventBus from "components/eventbus";
import _ from "lodash";
import store from "store";
import debounce from "v-debounce";
import Avatar from "components/avatar.vue";

// import VueRouter from "vue-router";
// import filters from "functions/FilterFunctions";
import TaskInput from "./TaskInput.vue";
import TaskList from "./TaskList.vue";
import Filters from "./Filters.vue";

// Vue.use(VueRouter);

const $ = window.jQuery;

// const routes = [
//   { path: "/all", component: TaskList, props: { filter: "all" } },
//   { path: "/completed", component: TaskList, props: { filter: "completed" } },
//   { path: "/active", component: TaskList, props: { filter: "active" } },
//   { path: "*", redirect: "/all" }
// ];
// const router = new VueRouter({
//   routes,
//   base: window.location.href,
//   linkActiveClass: "active"
// });

export default {
  store,
  directives: { debounce },
  components: {
    TaskInput,
    TaskList,
    Filters,
    Avatar,
  },

  props: {
    currentUserId: null,
    showSearch: {
      type: Boolean,
      default: false,
    },
    showFilters: {
      type: Boolean,
      default: false,
    },
    allowAdditions: {
      type: Boolean,
      default: true,
    },
    parentEvent: {
      type: Object,
      default: null,
    },
    initialTasks: Array,
    initialSorts: {
      type: Array,
      default() {
        return ["event", "user", "date", "name"];
      },
    },
    initialSort: {
      type: String,
      default: "event",
    },
    endpoint: String,
    // users: Array
  },
  data() {
    const $this = this;
    return {
      // users: [],
      showSuggested: false,
      filter: $this.getLocalStorage("filter", "all"),
      selectedSort: $this.getLocalStorage("selectedSort", $this.initialSort),
      sorts: {
        event: {
          object: { key: "event", label: "name", sort: "starts_at" },
          name: "Campaign Date",
        },
        user: {
          object: { key: "user", label: "login", sort: "user_login" },
          name: "User",
        },
        date: { object: { sort: "due_on" }, name: "Due Date" },
      },
      msg: "A new task",
      searchString: "",
      selectedUser: $this.getLocalStorage("selectedUser", null),
    };
  },
  computed: {
    ...mapState("task", {
      loading: "loading",
      error: (state) => state.error,
    }),
    ...mapState("calendar", ["calendars"]),
    ...mapGetters("calendar", {
      categories: "categories",
      users: "users",
    }),
    queryParams() {
      const $this = this;
      return {
        user_id: $this.selectedUserId,
        q: {
          user_login_or_event_name_or_name_cont: $this.searchString,
          task_status_eq: $this.filter,
        },
        sort: this.sorts[this.selectedSort].object.sort,
      };
    },

    selectedUserId() {
      return this.selectedUser && this.selectedUser.id;
    },
    newTask() {
      return this.$store.getters["task/new"](this.parentEvent);
    },
    tasks() {
      return this.$store.getters["task/all"](this.parentEvent);
    },
    suggestedEventTasks() {
      return this.$store.getters["task/suggestedEventTasks"](this.parentEvent);
    },
    availableSorts() {
      const $this = this;
      return _.pickBy($this.sorts, (value, key) =>
        $this.initialSorts.includes(key)
      );
    },
    selectedSortName() {
      return this.availableSorts[this.selectedSort].name;
    },
    isNativeApp() {
      return document.documentElement.hasAttribute("data-hotwire-native");
    },
    // Whether any toolbar child will actually render under the native
    // gating rules (search needs >5 tasks; sort/filter need >1). Used to
    // drop the toolbar band -- not just its children -- on native.
    toolbarHasItems() {
      const multi = this.tasks.length > 1;
      return (
        (this.showSearch && this.tasks.length > 5) ||
        (multi && Object.keys(this.availableSorts).length > 1) ||
        (multi && this.showFilters)
      );
    },
    group() {
      return (
        this.sorts[this.selectedSort] && this.sorts[this.selectedSort].object
      );
    },
    filteredTasks() {
      return this.tasks;
    },
    hiddenTasks() {
      return this.tasks.length - this.filteredTasks.length;
    },
    remaining() {
      return this.tasks.filter((t) => !t.completed).length;
    },
    showSaveAll() {
      return this.hasUnsavedTasks;
    },
    hasUnsavedTasks() {
      return _.some(this.tasks, (t) => !t.id);
    },
    areAllCompleted: {
      get() {
        return this.remaining === 0;
      },
      set(value) {
        this.tasks.map((task) => {
          this.toggleComplete(value, task);
          return task;
        });
      },
    },
  },
  watch: {
    selectedUser(val) {
      this.setLocalStorage("selectedUser", JSON.stringify(val));
    },
    selectedUserId() {
      this.loadPage(1);
    },
    selectedSort(val) {
      this.loadTasks();
      this.setLocalStorage("selectedSort", JSON.stringify(val));
    },
    filter(val) {
      this.setLocalStorage("filter", JSON.stringify(val));
      this.loadTasks();
    },
    hasUnsavedTasks(val) {
      if (val) {
        EventBus.$emit("eventModalDirty", "tasks");
      } else {
        EventBus.$emit("eventModalClean", "tasks");
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$store.dispatch("calendar/loadCalendars");

      if (this.initialTasks) {
        this.setTasks(this.initialTasks);
      } else {
        this.loadTasks();
      }

      this.initDropdowns();
    });
  },
  updated() {
    // this.initDropdowns();
    $(this.$el)
      .find(".right.floated.secondary.menu .dropdown")
      .dropdown("refresh");
  },
  methods: {
    // Native empty-state action: hand focus to the capture input (the same
    // input.new-task TaskInput itself focuses after a save).
    focusNewTask() {
      const wrap = this.$refs.newTaskInput && this.$refs.newTaskInput.$el;
      const input = wrap && wrap.querySelector("input.new-task");
      if (input) input.focus();
    },
    clearSearch() {
      this.searchString = "";
      this.loadPage(1);
    },
    changeFilter(val) {
      this.filter = val;
    },
    setTasks(tasks) {
      this.$store.dispatch("task/setAll", {
        items: tasks,
        parent: this.parentEvent,
      });
    },
    assignSuggestedTasksToUser(user) {
      this.$store.dispatch("task/assignSuggestedTo", user);
    },
    addSuggestedTasks(tasks) {
      this.showSuggested = false;
      this.$store.dispatch("task/setAllFromSuggested", {
        items: tasks,
        parent: this.parentEvent,
      });
    },
    showAll() {
      this.selectedUser = null;
    },
    showUsersTasks(user) {
      this.selectedUser = user;
    },
    saveAll() {
      this.$store.dispatch("task/saveAll");
    },
    initDropdowns() {
      $(this.$el)
        .find(".right.floated.secondary.menu .dropdown, .dropdown.icon.button")
        .dropdown()
        .data("dropdown-loaded", true);
    },

    loadTasks(page = 1) {
      this.$store.dispatch("task/loadAll", {
        page,
        parentEvent: this.parentEvent,
        queryParams: this.queryParams,
      });
    },
    loadPage(page = 1) {
      this.loadTasks(page);
    },
    localStorageKey(key) {
      let string = "";
      string = `tasks_${this.parentEvent ? this.parentEvent.id : "index"}`;

      return `${string}:${key}`;
    },
    setLocalStorage(key, val) {
      return localStorage.setItem(this.localStorageKey(key), val);
    },
    getLocalStorage(key, fallback) {
      const val = localStorage.getItem(this.localStorageKey(key));
      return val ? JSON.parse(val) : fallback;
    },
  },
  // router
};
</script>

<style lang="scss" scoped>
header {
  margin-bottom: 2em;
}

.ui.message {
  border: none !important;
  box-shadow: none !important;
}

// Empty state (native only, spec §13.4): a gray Fomantic slab with a fist
// icon top-left is replaced by a centered check-circle + two lines of copy.
// `.taskapp > .native-tasks-empty` (not just `.native-tasks-empty`) to
// out-specify the `.taskapp > div { flex-direction: column-reverse }` rule
// below, which otherwise renders icon/heading/body in reverse order.
.taskapp > .native-tasks-empty {
  display: block;
  padding: 48px 24px 24px;
  text-align: center;

  i.icon {
    display: block;
    margin: 0 auto 16px;
    font-size: 40px;
    color: var(--text-muted, #787a92);
  }

  h3 {
    margin: 0 0 8px;
    font-size: 17px;
    font-weight: 600;
  }

  p {
    margin: 0 auto;
    max-width: 280px;
    font-size: 15px;
    line-height: 1.45;
    color: var(--text-secondary, rgba(0, 0, 0, 0.6));
  }

  .native-tasks-empty-action {
    margin-top: 20px;
  }
}

.secondary.menu {
  span.nobreak {
    white-space: nowrap;
  }

  .dropdown.item.sorts {
    width: 140px;
  }
  .dropdown.item.filters {
    width: 120px;
  }
  .item.search {
    input {
      border-color: var(--bg-surface);
    }
  }
}

@media (max-width: 768px) {
  h4 {
    font-size: 1.42857143rem !important;
    padding: 0 !important;
    padding-left: 50px !important;
    margin-top: 0px !important;
  }

  .taskapp > div {
    display: flex;
    flex-direction: column-reverse;
  }

  .right.floated.menu {
    float: none;
    margin: 2em 1em 0 1em;
    .item {
      margin-bottom: 0.5em;
    }
    .item.search {
      margin-bottom: 1em;

      input {
        min-height: 44px;
      }
    }
  }

  // Native top rhythm (assessment: Tasks #2): with the empty toolbar band
  // gone (template v-if), the capture card is the first thing on screen --
  // pull it up to the page padding instead of the desktop 2em float-clear
  // spacing, and drop the 50px hamburger clearance mobile web needs (the
  // native shell has no floating in-page menu button).
  html[data-hotwire-native] header.header {
    margin-bottom: 12px;
  }

  html[data-hotwire-native] h4 {
    padding-left: 0 !important;
  }

  html[data-hotwire-native] .right.floated.menu {
    margin-top: 8px;
  }

  // Mobile task form: every control full-width with a 44px touch target.
  // ::v-deep because Fomantic wraps <select>s in generated .ui.selection
  // .dropdown divs that don't carry Vue's scope attribute.
  ::v-deep .ui.task.form .fields {
    display: block;
    margin-left: 0;
    margin-right: 0;

    .field {
      width: 100% !important;
      padding-left: 0;
      padding-right: 0;
      margin: 0 0 12px !important;

      input {
        min-height: 44px;
      }

      .ui.selection.dropdown {
        display: flex;
        align-items: center;
        width: 100% !important;
        min-height: 44px;
      }

      .buttons,
      .buttons .button {
        display: block !important;
        width: 100% !important;
        min-height: 44px;
      }
    }
  }
}
</style>
